/**
 * 复制字符串数组。
 *
 * @param values 原始字符串数组。
 * @returns 复制后的字符串数组。
 */
function cloneStringArray(values: string[] | undefined): string[] | undefined {
  return Array.isArray(values) ? values.filter((value) => typeof value === 'string') : undefined;
}

/**
 * 复制运行时面拓扑提示。
 *
 * @param hint 原始运行时面提示。
 * @returns 可序列化运行时面提示。
 */
function cloneRuntimeTopologyFaceHint(
  hint: RuntimeTopologyFaceHint,
): RuntimeTopologyFaceHint {
  return {
    subEntityId: hint.subEntityId,
    topologyIndex: hint.topologyIndex,
    shapeHash: hint.shapeHash,
    persistentRef: hint.persistentRef,
  };
}

/**
 * 复制运行时拓扑提示。
 *
 * 设计说明：
 * 1. 运行时提示只允许携带标量和字符串数组；
 * 2. 任何附加对象引用都必须在这里被丢弃，避免把 three 对象或 DOM 对象带入 worker 命令；
 * 3. 该函数保留当前求解所需的 topologyIndex、persistentRef、shapeHash 和相邻面提示。
 *
 * @param hint 原始运行时拓扑提示。
 * @returns 可序列化运行时拓扑提示。
 */
function cloneRuntimeTopologyHint(
  hint: RuntimeTopologyHint | undefined,
): RuntimeTopologyHint | undefined {
  if (!hint) {
    return undefined;
  }
  return {
    kind: hint.kind,
    subEntityId: hint.subEntityId,
    topologyIndex: hint.topologyIndex,
    shapeHash: hint.shapeHash,
    sourceObjectId: hint.sourceObjectId,
    persistentRef: hint.persistentRef,
    adjacentFaceRefs: cloneStringArray(hint.adjacentFaceRefs),
    adjacentFaceHints: Array.isArray(hint.adjacentFaceHints)
      ? hint.adjacentFaceHints.map(cloneRuntimeTopologyFaceHint)
      : undefined,
  };
}

/**
 * 复制圆角/倒角选择项。
 *
 * @param item 原始选择项。
 * @returns 可序列化选择项。
 */
export function cloneSelectionItemRefData(item: SelectionItemRef): SelectionItemRef {
  return {
    kind: item.kind,
    bodyObjectId: item.bodyObjectId,
    subEntityId: item.subEntityId,
    semanticName: item.semanticName,
    persistentRef: item.persistentRef,
    owningFeatureId: item.owningFeatureId,
    adjacentFaceRefs: cloneStringArray(item.adjacentFaceRefs),
    runtimeTopologyHint: cloneRuntimeTopologyHint(item.runtimeTopologyHint),
  };
}

/**
 * 复制帮助点引用。
 *
 * @param pointRef 原始帮助点引用。
 * @returns 可序列化帮助点引用。
 */
function cloneBlendPointRefData(pointRef: BlendPointRef | null | undefined): BlendPointRef | null {
  if (!pointRef) {
    return null;
  }
  return {
    bodyObjectId: pointRef.bodyObjectId,
    subEntityId: pointRef.subEntityId,
    point: [
      pointRef.point[0],
      pointRef.point[1],
      pointRef.point[2],
    ],
    persistentRef: pointRef.persistentRef,
    runtimeTopologyHint: cloneRuntimeTopologyHint(pointRef.runtimeTopologyHint),
    displayLabel: pointRef.displayLabel,
  };
}

/**
 * 复制局部边参数。
 *
 * @param partialEdge 原始局部边参数。
 * @returns 可序列化局部边参数。
 */
function clonePartialEdgeParamData(partialEdge: PartialEdgeParam): PartialEdgeParam {
  return {
    itemRef: partialEdge.itemRef,
    startCondition: partialEdge.startCondition,
    startValue: partialEdge.startValue,
    startExpression: partialEdge.startExpression,
    startReferencePoint: cloneBlendPointRefData(partialEdge.startReferencePoint),
    endCondition: partialEdge.endCondition,
    endValue: partialEdge.endValue,
    endExpression: partialEdge.endExpression,
    endReferencePoint: cloneBlendPointRefData(partialEdge.endReferencePoint),
  };
}

/**
 * 复制变量圆角控制点。
 *
 * @param point 原始控制点。
 * @returns 可序列化控制点。
 */
function cloneVariableRadiusControlPointData(
  point: VariableRadiusControlPoint,
): VariableRadiusControlPoint {
  return {
    key: point.key,
    itemRef: point.itemRef,
    label: point.label,
    role: point.role,
    vertexRef: point.vertexRef,
    instanceIndex: point.instanceIndex,
    parameter: point.parameter,
    radius: point.radius,
    expression: point.expression,
    radius2: point.radius2,
    expression2: point.expression2,
  };
}

/**
 * 复制面集合选择。
 *
 * @param faceSet 原始面集合。
 * @returns 可序列化面集合。
 */
function cloneFaceSetSelectionData(faceSet: FaceSetSelection): FaceSetSelection {
  return {
    setId: faceSet.setId,
    items: (faceSet.items ?? []).map(cloneSelectionItemRefData),
  };
}

/**
 * 复制圆角定义。
 *
 * @param definition 原始圆角定义。
 * @returns 可序列化圆角定义。
 */
export function cloneFilletFeatureDefinitionData(
  definition: FilletFeatureDefinition,
): FilletFeatureDefinition {
  return {
    type: definition.type,
    tangentPropagation: definition.tangentPropagation,
    selectThroughFaces: definition.selectThroughFaces,
    keepFeatures: definition.keepFeatures,
    items: (definition.items ?? []).map(cloneSelectionItemRefData),
    boundaryItems: Array.isArray(definition.boundaryItems)
      ? definition.boundaryItems.map(cloneSelectionItemRefData)
      : undefined,
    boundaryRefs: cloneStringArray(definition.boundaryRefs),
    radius: definition.radius,
    radiusExpression: definition.radiusExpression,
    radius2: definition.radius2,
    radius2Expression: definition.radius2Expression,
    chordWidth: definition.chordWidth,
    chordWidthExpression: definition.chordWidthExpression,
    overflowType: definition.overflowType,
    edgeFilletMode: definition.edgeFilletMode,
    asymmetricRadiiFlipped: definition.asymmetricRadiiFlipped,
    partialEdgeParametersEnabled: definition.partialEdgeParametersEnabled,
    partialEdges: Array.isArray(definition.partialEdges)
      ? definition.partialEdges.map(clonePartialEdgeParamData)
      : undefined,
    variablePoints: Array.isArray(definition.variablePoints)
      ? definition.variablePoints.map(cloneVariableRadiusControlPointData)
      : undefined,
    variableInstanceCount: definition.variableInstanceCount,
    variableMode: definition.variableMode,
    variableProfile: definition.variableProfile,
    variableTransition: definition.variableTransition,
    faceSets: Array.isArray(definition.faceSets)
      ? definition.faceSets.map(cloneFaceSetSelectionData)
      : undefined,
    helpPoint: cloneBlendPointRefData(definition.helpPoint),
    faceFilletMode: definition.faceFilletMode,
    faceFilletRadiiFlipped: definition.faceFilletRadiiFlipped,
    trimSurfaces: definition.trimSurfaces,
    constantWidth: definition.constantWidth,
  };
}

/**
 * 复制倒角定义。
 *
 * @param definition 原始倒角定义。
 * @returns 可序列化倒角定义。
 */
export function cloneChamferFeatureDefinitionData(
  definition: ChamferFeatureDefinition,
): ChamferFeatureDefinition {
  return {
    type: definition.type,
    tangentPropagation: definition.tangentPropagation,
    selectThroughFaces: definition.selectThroughFaces,
    keepFeatures: definition.keepFeatures,
    items: (definition.items ?? []).map(cloneSelectionItemRefData),
    distance: definition.distance,
    distanceExpression: definition.distanceExpression,
    distance2: definition.distance2,
    distance2Expression: definition.distance2Expression,
    width: definition.width,
    widthExpression: definition.widthExpression,
    angleDeg: definition.angleDeg,
    angleExpression: definition.angleExpression,
    symmetric: definition.symmetric,
    equalDistance: definition.equalDistance,
    vertexDistances: definition.vertexDistances
      ? [
        definition.vertexDistances[0],
        definition.vertexDistances[1],
        definition.vertexDistances[2],
      ]
      : null,
    vertexDistanceExpressions: definition.vertexDistanceExpressions
      ? [
        definition.vertexDistanceExpressions[0],
        definition.vertexDistanceExpressions[1],
        definition.vertexDistanceExpressions[2],
      ]
      : null,
    partialEdgeParametersEnabled: definition.partialEdgeParametersEnabled,
    partialEdges: Array.isArray(definition.partialEdges)
      ? definition.partialEdges.map(clonePartialEdgeParamData)
      : undefined,
    faceSets: Array.isArray(definition.faceSets)
      ? definition.faceSets.map(cloneFaceSetSelectionData)
      : undefined,
    faceFaceMode: definition.faceFaceMode,
    flipDirection: definition.flipDirection,
    helpPoint: cloneBlendPointRefData(definition.helpPoint),
  };
}
